import { IOption } from '../../../common/common.types'
import { INemsisSnomedLookup } from '../../../components/procedure/store/procedure.types'
import { randomNumber } from '../../../utilities'
import { TimelineConstants } from '../../timeline/store/timeline.constants'
import { IPatientVitals } from '../../vitals/store/vitals.types'
import {
  ICustomData,
  ICustomElement,
  ICustomElements,
  ICustomTimeLineItem,
  ICustomTimeLineItems,
  INemsisCustomElementsGroup,
  INemsisCustomElementsGroups,
  IResultOption,
  IStateCustomElements,
  IStateResult,
} from '../store/nemsisCustomData.types'

export const procedureToPCRLocationMap = [
  { name: TimelineConstants.IMAGING_ULTRASOUND_PROCEDURE, location: 'imagingUltrasoundStateList' },
  { name: TimelineConstants.AIRWAY_PROCEDURE, location: 'airwayStateList' },
  { name: TimelineConstants.VENTILATOR_PROCEDURE, location: 'ventilatorStateList' },
  { name: TimelineConstants.CARDIAC_PROCEDURE, location: 'cardiacStateList' },
  { name: TimelineConstants.DRAIN_PROCEDURE, location: 'drainStateList' },
  { name: TimelineConstants.INITIATE_IV_PROCEDURE, location: 'initiateIvStateList' },
  { name: TimelineConstants.HOSPITAL_NOTIFY_PROCEDURE, location: 'hospitalNotifyStateList' },
  { name: TimelineConstants.INTUBATION_PROCEDURE, location: 'intubationStateList' },
  { name: TimelineConstants.LABS_PROCEDURE, location: 'labsStateList' },
  { name: TimelineConstants.OPERATIONS_PROCEDURE, location: 'operationsStateList' },
  { name: TimelineConstants.CHILDBIRTH_PROCEDURE, location: 'childbirthOBStateList' },
  { name: TimelineConstants.EXTRICATION_PROCEDURE, location: 'extricationStateList' },
  { name: TimelineConstants.IMMOBILIZATION_PROCEDURE, location: 'immobilizationStateList' },
  { name: TimelineConstants.MEDICAL_CONSULT_PROCEDURE, location: 'medicalConsultStateList' },
  { name: TimelineConstants.RESCUE_PROCEDURE, location: 'rescueStateList' },
  { name: TimelineConstants.WOUND_CARE_PROCEDURE, location: 'woundCareStateList' },
  { name: TimelineConstants.VENTRICULAR_ASSIST_DEVICE_PROCEDURE, location: 'ventricularAssistDeviceStateList' },
  { name: TimelineConstants.ASSESSMENT, location: 'assessmentsStateList' },
]

/**
 * Remove duplicate groups with no data.
 * @param {INemsisCustomElementsGroups} All groups
 */
const removeEmptyGroupsData = (groups: INemsisCustomElementsGroups) => {
  const groupedById = groups.reduce<Record<string, typeof groups[number][]>>((acc, group) => {
    const groupKey = group.groupId ?? ''
    if (!acc[groupKey]) acc[groupKey] = []
    acc[groupKey].push(group)
    return acc
  }, {})

  const removeEmptyGroups = Object.values(groupedById).flatMap((groups) => {
    const filteredGroups = groups.filter((group: INemsisCustomElementsGroup) => {
      if (group.groupingSequence === 1 || group.procedureKey) return true
      return group.customElements.some(
        (element) =>
          element.resultsPNNV ||
          (element?.results && Object.keys(element?.results).length > 0) ||
          element?.results?.length
      )
    })

    return filteredGroups.map((group: INemsisCustomElementsGroup, index: number) => ({
      ...group,
      groupingSequence: index + 1,
    }))
  })
  return removeEmptyGroups
}

/**
 * Apply the saved data to custom display data.
 * @param {IStateCustomElements} All original custom elements
 * @param {stateElementsPcr} Custom display data stored in PCR
 */
export const populateCustomElementsFromPCR = (
  stateElements: IStateCustomElements,
  stateElementsPcr?: IStateCustomElements
): IStateCustomElements => {
  if (!stateElementsPcr || stateElementsPcr.length === 0) return stateElements

  return stateElements.map((state) => {
    const matchingPcrState = stateElementsPcr.find((pcr) => pcr.state === state.state)
    if (!matchingPcrState) return state

    const updatedGroups = state.groups.flatMap((group) => {
      const matchingPcrGroups = matchingPcrState.groups.filter((pcrGroup) => pcrGroup.groupId === group.groupId)

      if (matchingPcrGroups.length === 0) return [group]

      return matchingPcrGroups.map((pcrGroup) => {
        const updatedCustomElements = group.customElements.map((customElement) => {
          const matchingPcrElement = pcrGroup.customElements.find(
            (pcrElement) => pcrElement.id === customElement.id && pcrElement.key === customElement.key
          )

          if (matchingPcrElement) {
            return {
              ...customElement,
              results: matchingPcrElement.results ?? customElement.results,
              resultsPNNV: matchingPcrElement.resultsPNNV ?? customElement.resultsPNNV,
              _pnnvToggle: matchingPcrElement._pnnvToggle ?? customElement._pnnvToggle,
            }
          } else {
            return customElement
          }
        })

        const missingCustomElements = pcrGroup.customElements
          .filter(
            (pcrElement) =>
              !group.customElements.some((existing) => existing.id === pcrElement.id && existing.key === pcrElement.key)
          )
          .map((newElement) => {
            const isDuplicateGroup = group.groupingSequence !== pcrGroup.groupingSequence
            const existingElementWithSameId = group.customElements.find((existing) => existing.id === newElement.id)

            return {
              ...newElement,
              key: isDuplicateGroup ? randomNumber().toString() : newElement.key,
              mergedoptions: existingElementWithSameId?.mergedoptions ?? [],
            }
          })

        const finalCustomElements = [
          ...updatedCustomElements,
          ...missingCustomElements.filter((newElement) => {
            const existingElement = updatedCustomElements.find(
              (ele) => ele.id === newElement.id && !newElement.isDuplicated
            )
            return !existingElement || !existingElement.results
          }),
        ]

        const groupedElements = finalCustomElements.reduce((acc, element) => {
          const key = element.id
          if (!acc[key]) acc[key] = []
          acc[key].push(element)
          return acc
        }, {} as Record<string, ICustomElement[]>)

        const updatedFinalCustomElements = Object.values(groupedElements).flatMap((elements) => {
          elements.sort((a, b) => a.customElementSequence - b.customElementSequence)
          return elements
        })

        return {
          ...group,
          groupingSequence: pcrGroup.groupingSequence,
          customElements: updatedFinalCustomElements,
        }
      })
    })

    const newGroups = matchingPcrState?.groups
      .filter((pcrGroup) => {
        if (pcrGroup.procedureKey) return true
        return !state.groups?.some((existing) => existing.groupKey === pcrGroup.groupKey)
      })
      .map((pcrGroup) => ({
        ...pcrGroup,
        customElements: pcrGroup.customElements.map((newElement) => {
          const existingElementWithSameId = state.groups
            .flatMap((g) => g.customElements)
            .find((el) => el.id === newElement.id)
          return {
            ...newElement,
            mergedoptions: existingElementWithSameId?.mergedoptions ?? [],
          }
        }),
      }))

    const groupedByGroupId = updatedGroups.reduce((acc: Record<string, INemsisCustomElementsGroup[]>, group) => {
      const key = group.groupId ?? ''
      if (!acc[key]) acc[key] = []
      acc[key].push(group)
      return acc
    }, {} as Record<string, INemsisCustomElementsGroup[]>)

    Object.keys(groupedByGroupId).forEach((groupId: string) => {
      const groups = groupedByGroupId[groupId]

      groups.sort((a, b) => a.groupingSequence - b.groupingSequence)

      const seenKeys = new Set()

      groups.forEach((group: { customElements: ICustomElements }) => {
        group.customElements = group.customElements.filter((element) => {
          if (seenKeys.has(element.key)) {
            return false
          }
          seenKeys.add(element.key)
          return true
        })
      })
    })
    const mergedGroups = updatedGroups.map((updatedItem) => {
      const matchingIndex = newGroups.findIndex(
        (newItem) =>
          newItem.groupId === updatedItem.groupId && newItem.groupingSequence === updatedItem.groupingSequence
      )

      if (matchingIndex !== -1) {
        const matchedItem = newGroups[matchingIndex]

        newGroups.splice(matchingIndex, 1)

        return matchedItem
      }

      return updatedItem
    })

    const groups = [...mergedGroups, ...newGroups]
    const filteredGroups = removeEmptyGroupsData(groups)

    return {
      ...state,
      groups: filteredGroups,
    }
  })
}

/**
 * Get the index of the final instance of a group.
 * @param {INemsisCustomElementsGroups} groups The list of groups to search over
 * @param {groupId} groupId The ID of the group to search for
 */
export const getLastGroupInstanceIndex = (groups: INemsisCustomElementsGroups, groupId: string | null): number => {
  let lastGroupInstanceIndex = 0
  groups.forEach((group, i) => {
    if (group.groupId === groupId) {
      lastGroupInstanceIndex = i
    }
  })

  return lastGroupInstanceIndex
}

/**
 * Re arrange the sequence in case some groups are created without any data
 * @param {ICustomData} Custom data for mapping
 */
const rearrangeGroupingSequences = (data: ICustomData) => {
  return data.map((stateEntry) => {
    const groups: Record<string, IStateResult[]> = {}

    stateEntry.stateResults.forEach((entry) => {
      const groupKey = entry.groupId ?? ''
      if (!groups[groupKey]) groups[groupKey] = []
      groups[groupKey].push(entry)
    })

    Object.keys(groups).forEach((groupId) => {
      const sortedEntries = groups[groupId].sort((a, b) => a.groupingSequence - b.groupingSequence)
      const sequenceMap = new Map()
      let newSequence = 1

      sortedEntries.forEach((entry) => {
        if (!sequenceMap.has(entry.groupingSequence)) {
          sequenceMap.set(entry.groupingSequence, newSequence)
          newSequence++
        }
      })

      sortedEntries.forEach((entry) => {
        entry.groupingSequence = sequenceMap.get(entry.groupingSequence)
        entry.groupCorrelation = entry.groupId ? `${entry.groupId}${entry.groupingSequence}` : ''
      })
    })

    return stateEntry
  })
}

/** Tranformation util for pcr mapping
 * @param  {IStateCustomElements} entire custom state data
 */
export const convertToFormattedStateResults = (stateCustomElements: IStateCustomElements): ICustomData => {
  const transformedData = [...stateCustomElements]
    .map((stateCustomElement) => {
      const { state, groups } = stateCustomElement
      const stateResultsMap = new Map<string, IStateResult>()
      groups?.forEach((group) => {
        group.customElements.forEach((customElement) => {
          const formatResult =
            customElement?.results?.length && typeof customElement.results === 'string'
              ? { valueText: customElement?.results }
              : customElement.results

          if (customElement?.results || customElement?.resultsPNNV) {
            const key = `${state}-${group.groupKey}-${group.groupId}-${group.groupingSequence}-${customElement.id}`
            if (!stateResultsMap.has(key)) {
              stateResultsMap.set(key, {
                customElementId: customElement.id,
                customElementName: customElement.nemsisCode,
                clientCorrelationId: customElement?.clientCorrelationId || '',
                originalDataPath: '',
                groupId: group.groupId,
                version: customElement.version,
                mapToNemsis3: customElement.mapToNemsis3,
                elementNumber: customElement.elementNumber,
                groupingSequence: group.groupingSequence,
                groupCorrelation: group.groupId ? `${group.groupId}${group.groupingSequence}` : '',
                results: formatResult as IResultOption | IResultOption[] | null,
                resultsPNNV: customElement.resultsPNNV ?? null,
                _groupKey: group.groupKey,
              })
            } else {
              const existingEntry: IStateResult = stateResultsMap.get(key)!
              const result = [
                ...(Array.isArray(existingEntry.results) ? existingEntry.results : [existingEntry.results]),
                ...(Array.isArray(formatResult) ? formatResult : [formatResult]),
              ].filter(Boolean)

              if (result.length > 1) {
                existingEntry.results = result.map((item) =>
                  typeof item === 'object' && item !== null && 'valueText' in item ? item : { valueText: item }
                ) as IResultOption[]
              }
            }
          }
        })
      })

      return {
        state,
        stateResults: Array.from(stateResultsMap.values()),
      }
    })
    .filter((stateResult) => stateResult.stateResults.length > 0)

  const removeStaleData = transformedData
    .map((stateObj) => {
      if (!stateObj) return { stateResults: [], state: '' }
      const filteredStateResults = stateObj.stateResults.filter((sr) => {
        const { results, resultsPNNV } = sr

        return (
          (Array.isArray(results) && results.length > 0) || (results && Object.keys(results).length > 0) || resultsPNNV
        )
      })
      return { ...stateObj, stateResults: filteredStateResults }
    })
    .filter((stateObj) => stateObj.stateResults.length > 0)
  const rearrangeGroups = rearrangeGroupingSequences(removeStaleData)
  return rearrangeGroups
}

/** Add groups based on added timeline items, restricted to specific datasets.
 * @param  {ICustomTimeLineItems} timelineItems - Timeline items (vital / procedures / medications)
 * @param  {IStateCustomElements} stateCustomElementData - Entire custom state data (original data used for referencing group placements)
 * @param  {IStateCustomElements} modifiedStateCustomElementData - Modified state custom elements
 * @param  {Array} validDatasets - List of datasets defining where groups should be added
 */
export const createProcedureVitalGroups = (
  timelineItems: ICustomTimeLineItems,
  stateCustomElementData: IStateCustomElements,
  modifiedStateCustomElementData: IStateCustomElements,
  validDatasets: Array<{ dataset: string; version: string; groupIdName: string }>
): IStateCustomElements => {
  const procedureGroupTriggers: Record<string, string[]> = {}

  const validStateGroups = new Map()
  validDatasets.forEach(({ dataset, groupIdName, version }) => {
    validStateGroups.set(`${dataset}-${groupIdName}-${version}`, true)
  })

  timelineItems.forEach((item: ICustomTimeLineItem) => {
    let groupNames: string[] = []

    if (item.itemType === 'Medications') {
      groupNames = ['eMedications.MedicationGroup']
    } else if (item.itemType === 'Procedures') {
      groupNames =
        item.event === 'Intubation'
          ? ['eProcedures.ProcedureGroup', 'eAirway.ConfirmationGroup']
          : ['eProcedures.ProcedureGroup']
    } else if (item.itemType === 'Vitals') {
      groupNames = ['eVitals.VitalGroup']
    }

    groupNames.forEach((groupName) => {
      if (!procedureGroupTriggers[groupName]) {
        procedureGroupTriggers[groupName] = []
      }
      procedureGroupTriggers[groupName].push(item.event || '')
    })
  })

  const result: IStateCustomElements = JSON.parse(JSON.stringify(modifiedStateCustomElementData))

  result.forEach((modifiedState) => {
    const { state, groups: modifiedGroups } = modifiedState

    const originalState = stateCustomElementData.find((original) => original.state === state)
    if (!originalState) return

    const originalGroups = originalState.groups
    const newGroupsForState: INemsisCustomElementsGroups = []

    originalGroups.forEach((originalGroup) => {
      const groupId = originalGroup.groupId || ''
      const triggeredByProcedures = procedureGroupTriggers[groupId]
      const version = originalGroup.customElements[0]?.version || ''

      if (!validStateGroups.has(`${state}-${groupId}-${version}`)) {
        return
      }

      if (triggeredByProcedures) {
        let groupSequence = Math.max(
          ...modifiedGroups.filter((group) => group.groupId === groupId).map((group) => group.groupingSequence || 0),
          0
        )

        const processedProcedures = new Set<string>()
        triggeredByProcedures.forEach((triggerProcedure) => {
          const relevantProcedures = timelineItems.filter((item) => item.event === triggerProcedure)

          relevantProcedures.forEach((procedure) => {
            const procedureKey = procedure.key || ''
            const procedureKey2 = procedure.key2 || ''
            const procedureIdentifier = `${procedureKey}-${procedureKey2}`

            const procedureName = procedure.event
            const isIntubation = procedure.event === 'Intubation'
            const clientCorrelationId = procedure.clientCorrelationId
            if (!processedProcedures.has(procedureIdentifier)) {
              const groupExists = modifiedGroups.some((existingGroup) => {
                return (
                  existingGroup.groupId === groupId &&
                  existingGroup.procedureKey === procedureKey &&
                  existingGroup.procedureKey2 === procedureKey2
                )
              })

              if (!groupExists && clientCorrelationId) {
                const newGroup: INemsisCustomElementsGroup = {
                  ...originalGroup,
                  groupKey: randomNumber().toString(),
                  groupingSequence: ++groupSequence,
                  procedureKey,
                  procedureKey2,
                  procedureName,
                  customElements: originalGroup.customElements.map((element) => ({
                    ...element,
                    key: randomNumber().toString(),
                    clientCorrelationId,
                    results: [],
                    resultsPNNV: null,
                    customElementType: getUpdatedCustomElementType(element),
                  })),
                }
                newGroupsForState.push(newGroup)
              }
              if (isIntubation && !groupExists && groupId === 'eAirway.ConfirmationGroup') {
                const newGroup: INemsisCustomElementsGroup = {
                  ...originalGroup,
                  groupKey: randomNumber().toString(),
                  groupingSequence: ++groupSequence,
                  procedureKey,
                  procedureKey2,
                  procedureName,
                  customElements: originalGroup.customElements.map((element) => ({
                    ...element,
                    key: randomNumber().toString(),
                    clientCorrelationId: `patient.intubationStateList_${procedure.key}`,
                    results: [],
                    resultsPNNV: null,
                    customElementType: getUpdatedCustomElementType(element),
                  })),
                }
                newGroupsForState.push(newGroup)
              }

              processedProcedures.add(procedureIdentifier)
            }
          })
        })
      }
    })
    modifiedState.groups = [...modifiedGroups, ...newGroupsForState]
  })

  return arrangeGroupsByTimeline(result, timelineItems)
}

/** Delete groups when relevant timeline items are deleted
 * @param  {IItems} Timeline items (vital / procedures / medications)
 * @param  {IStateCustomElements} entire custom state modified data
 */
export const deleteGroups = (
  timelineItems: ICustomTimeLineItems,
  stateCustomElementData: IStateCustomElements
): IStateCustomElements => {
  const updatedProcedureKeys = new Set(
    timelineItems.map((item) => (item.itemType === 'Medications' ? item.key2 : item.key))
  )
  const result: IStateCustomElements = JSON.parse(JSON.stringify(stateCustomElementData))

  result.forEach((stateData) => {
    const { groups } = stateData
    const filteredGroups = groups.filter((group) =>
      group?.groupId === 'eMedications.MedicationGroup'
        ? updatedProcedureKeys.has(group?.procedureKey2)
        : updatedProcedureKeys.has(group.procedureKey) || group.procedureKey === '' || !group.procedureKey
    )

    const groupsByGroupId: Record<string, INemsisCustomElementsGroup[]> = {}

    filteredGroups.forEach((group) => {
      const groupId = group.groupId || ''
      if (!groupsByGroupId[groupId]) {
        groupsByGroupId[groupId] = []
      }
      groupsByGroupId[groupId].push(group)
    })

    Object.keys(groupsByGroupId).forEach((groupId) => {
      const sortedGroups = groupsByGroupId[groupId].sort((a, b) => {
        const aIndex = timelineItems.findIndex((item) => item.key === a.procedureKey)
        const bIndex = timelineItems.findIndex((item) => item.key === b.procedureKey)
        return aIndex - bIndex
      })

      sortedGroups.forEach((group, index) => {
        group.groupingSequence = index + 1
      })
    })

    stateData.groups = filteredGroups
  })
  const reorderdStateElements = arrangeGroupsByTimeline(result, timelineItems)
  return reorderdStateElements
}

const getUniqueGroupIdsInOrder = (groups: INemsisCustomElementsGroups): string[] => {
  const seen = new Set<string>()
  const result: string[] = []
  for (const group of groups) {
    const groupId = group.groupId || ''
    if (!seen.has(groupId)) {
      seen.add(groupId)
      result.push(groupId)
    }
  }
  return result
}

/**
 * Reorder the custom Element Procedure groups data
 * @param {stateElements} All state elements populated with the saved data
 * @param {timelineItems} All timeline items added from Add Timeline Page
 */
export function arrangeGroupsByTimeline(
  stateElements: IStateCustomElements,
  timelineItems: ICustomTimeLineItems
): IStateCustomElements {
  return stateElements.map((stateElement) => {
    const originalGroups = stateElement.groups
    const uniqueGroupIds = getUniqueGroupIdsInOrder(originalGroups)
    const groupedGroups = new Map<string, INemsisCustomElementsGroups>()

    for (const group of originalGroups) {
      const groupId = group.groupId || ''
      const groupCopy = { ...group }
      if (!groupedGroups.has(groupId)) {
        groupedGroups.set(groupId, [])
      }
      groupedGroups.get(groupId)!.push(groupCopy)
    }

    const processedGroups: INemsisCustomElementsGroups = []

    for (const groupId of uniqueGroupIds) {
      const groupsOfType = groupedGroups.get(groupId) || []
      const groupsWithIndices = groupsOfType.map((group) => {
        const timelineItem = timelineItems.find((item) => item.key === group.procedureKey)
        let procedureDateTime = ''
        if (timelineItem?.labelHeader && timelineItem?.subHeader) {
          procedureDateTime = `: ${timelineItem?.labelHeader} (${timelineItem?.subHeader})`
        } else if (timelineItem?.labelHeaderFlag === 'PTA') {
          procedureDateTime = ': PTA'
        } else {
          procedureDateTime = 'Not Known'
        }
        const index = timelineItem ? timelineItems.indexOf(timelineItem) : -1
        return { group: { ...group, procedureDateTime: group.procedureKey ? procedureDateTime : '' }, index }
      })

      groupsWithIndices.sort((a, b) => {
        if (a.index === -1 && b.index === -1) return 0
        if (a.index === -1) return 1
        if (b.index === -1) return -1
        return a.index - b.index
      })

      let sequence = 1
      const sortedGroups = groupsWithIndices.map(({ group }) => {
        const updatedGroup = { ...group, groupingSequence: sequence++ }
        return updatedGroup
      })

      processedGroups.push(...sortedGroups)
    }

    return {
      ...stateElement,
      groups: processedGroups,
    }
  })
}

type IFilters = Record<string, unknown>

/**
 * Check if procedure has SNOMED code and generate client correlation id
 * @param {INemsisSnomedLookup} Procedure and action values for SNOMED Code generation
 * @param {procedure} Procedure name
 * @param {filters} Other field values apart from action / method
 * @param {key} Procedure key for event id generation
 */
export const generateClientCorrelationId = (
  options: INemsisSnomedLookup[],
  procedure: string,
  action: string | string[] | null,
  filters: IFilters,
  key: string
): string => {
  const matches = (optionValue: string | null, filterValue: any) =>
    optionValue === 'Any Value' ||
    (typeof optionValue === 'string' &&
      optionValue
        .split(',')
        .map((val) => val.trim())
        .includes(filterValue))

  const actionMatches = (optionAction: string | null, inputAction: string | string[] | null) => {
    if (optionAction === null && (inputAction === null || !inputAction?.length)) return true
    if (!inputAction || (Array.isArray(inputAction) && inputAction.length === 0)) return false
    if (typeof inputAction === 'string') return optionAction === inputAction || matches(optionAction, inputAction)
    return inputAction.some((act) => optionAction === act || matches(optionAction, act))
  }

  const matchedOption = options.find(
    (option) =>
      option.procedure === procedure &&
      actionMatches(option.action, action) &&
      Object.keys(filters).every((filterKey) =>
        matches(String(option[filterKey as keyof INemsisSnomedLookup]), String(filters[filterKey]))
      )
  )

  const code = matchedOption ? matchedOption.code : null

  const procedureLocation = procedureToPCRLocationMap.find((location) => location.name === procedure)
  return code ? `patient.${procedureLocation?.location}_${key}` : ''
}

/**
 * Remove unwanted data while saving custom display data
 * @param {IStateCustomElements} all state custom elements
 */
export const removeUnwantedData = (stateCustomElements: IStateCustomElements) => {
  return stateCustomElements
    .map((state) => {
      const filteredGroups = state.groups
        .map((group) => {
          const filteredCustomElements = group.customElements
            .filter(
              () =>
                group.procedureKey ||
                group.customElements.some(
                  (element) =>
                    element.resultsPNNV ||
                    (element?.results && Object.keys(element?.results).length > 0) ||
                    element?.results?.length
                )
            )
            .map(({ mergedoptions, ...rest }) => rest)

          return filteredCustomElements.length > 0 ? { ...group, customElements: filteredCustomElements } : null
        })
        .filter((group) => group !== null)

      return filteredGroups.length > 0 ? { ...state, groups: filteredGroups } : null
    })
    .filter((state) => state !== null)
}

/**
 * Check if Vital has valid data in fields to generate a vital group
 * @param {isValidVitalData} Vital data
 */
export const isValidVitalData = (data: IPatientVitals): boolean => {
  const hasValue = (value: unknown): boolean => value !== null && value !== undefined && value !== ''

  const hasNemsis3 = (value: IOption | null): boolean =>
    hasValue(value) && typeof value === 'object' && ('nemsis3' in value! || 'pn' in value! || 'nv' in value!)

  return (
    hasValue(data.regularity) ||
    (hasValue(data.spo2) && data.spo2 !== '0') ||
    hasValue(data.respirationRate) ||
    hasNemsis3(data.respirationRateEffort) ||
    hasValue(data.spCO) ||
    hasValue(data.glucose) ||
    hasNemsis3(data.levelOfConsciousness) ||
    hasValue(data.totalGCSScore) ||
    hasValue(data.rts) ||
    hasValue(data.etCO2) ||
    hasValue(data.etCO2Type) ||
    hasValue(data.pain) ||
    hasValue(data.temperature) ||
    hasNemsis3(data.temperatureMethod) ||
    hasValue(data.eyeOpeningResponse) ||
    hasValue(data.verbalResponse) ||
    hasValue(data.motorResponse) ||
    hasValue(data.heartRate) ||
    (hasValue(data?.heartRateMethod) && data?.heartRateMethod?.id && data?.heartRateMethod.id > 0) ||
    (hasValue(data.isEkgImport) && data.isEkgImport === 1) ||
    hasValue(data.systolicBP) ||
    hasValue(data.diastolicBP) ||
    hasValue(data.mapVital) ||
    hasNemsis3(data.bloodPressureMethod) ||
    hasNemsis3(data.bloodPressureMethodPNNV) ||
    ('nemsis35_evitals29' in (data.strokeScale ?? {}) && 'nemsis35_evitals30' in (data.strokeScale ?? {})) ||
    ('nemsis35_evitals29' in (data.strokeScalePNNV ?? {}) && 'nemsis35_evitals30' in (data.strokeScalePNNV ?? {}))
  )
}

/**
 * Assign proper data types to custom element
 * @param {ICustomElement} Vital data
 */
export const getUpdatedCustomElementType = (customElement: ICustomElement) => {
  const { dataType, recurrence, mergedoptions = [] } = customElement
  const isNotPicklist = dataType === 'Other' && mergedoptions?.every((option) => option.isPN || option.isNV)
  const isPicklist = dataType !== 'Other' && mergedoptions?.some((option) => !option.isPN && !option.isNV)

  if ((isPicklist || dataType === 'Other') && !isNotPicklist) {
    return recurrence === 0 || dataType === 'Boolean' ? 'SINGLEPICKLIST' : 'MULTIPICKLIST'
  }

  switch (dataType) {
    case 'Text':
      return recurrence === 0 ? 'TEXT' : 'MULTITEXT'
    case 'Number':
      return recurrence === 0 ? 'NUMBER' : 'MULTINUMBER'
    case 'DATETIME':
      return recurrence === 0 ? 'DATETIME' : 'MULTIDATETIME'
    case 'Other':
      return isNotPicklist ? (recurrence === 0 ? 'TEXT' : 'MULTITEXT') : ''
    default:
      return ''
  }
}
